import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { generateResumeContent, extractKeywords } from '../services/openaiService';
import templateService from '../services/templateService';
import { parseResumeFile, testResumeParsingWithText } from '../services/resumeParserService';
import { saveAs } from 'file-saver';
import './ResumeBuilderPage.css';
import Header from './Header';
import Footer from './Footer';
import ProductSidebar from './ProductSidebar';

// Import template preview images
import ProfessionalTemplatePreview from './images/template-previews/professional.png';
import ModernTemplatePreview from './images/template-previews/professional.png';
import CreativeTemplatePreview from './images/template-previews/professional.png';
import AcademicTemplatePreview from './images/template-previews/professional.png';

// Default resume templates
const RESUME_TEMPLATES = [
  {
    id: 1,
    name: 'Professional',
    preview: ProfessionalTemplatePreview,
    description: 'Clean and professional design suitable for corporate roles'
  },
  {
    id: 2,
    name: 'Modern',
    preview: ModernTemplatePreview,
    description: 'Contemporary design with modern layout and styling'
  },
  {
    id: 3,
    name: 'Creative',
    preview: CreativeTemplatePreview,
    description: 'Creative layout ideal for design and marketing roles'
  },
  {
    id: 4,
    name: 'Academic',
    preview: AcademicTemplatePreview,
    description: 'Formal design perfect for academic and research positions'
  }
];

// Accordion Section Component
const AccordionSection = ({ id, title, icon, isOpen, onToggle, disabled, badge, children }) => {
  return (
    <div className={`accordion-section ${isOpen ? 'accordion-open' : ''} ${disabled ? 'accordion-disabled' : ''}`}>
      <button
        className="accordion-header"
        onClick={() => !disabled && onToggle(id)}
        disabled={disabled}
        type="button"
      >
        <div className="accordion-header-left">
          <span className="accordion-icon">{icon}</span>
          <span className="accordion-title">{title}</span>
          {badge && <span className="accordion-badge">{badge}</span>}
        </div>
        <div className="accordion-header-right">
          <span className={`accordion-chevron ${isOpen ? 'chevron-open' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </button>
      <div className={`accordion-body ${isOpen ? 'accordion-body-open' : ''}`}>
        <div className="accordion-content">
          {children}
        </div>
      </div>
    </div>
  );
};

// Keywords Popup Component
const KeywordsPopup = ({ keywords, onClose }) => {
  return (
    <div className="keywords-popup-overlay" onClick={onClose}>
      <div className="keywords-popup" onClick={(e) => e.stopPropagation()}>
        <div className="keywords-popup-header">
          <div className="keywords-popup-title">
            <span className="keywords-popup-icon">🔑</span>
            <h3>Extracted Keywords</h3>
            <span className="keyword-count-badge">{keywords.length}</span>
          </div>
          <button className="keywords-popup-close" onClick={onClose} type="button">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="keywords-popup-body">
          <div className="keywords-list">
            {keywords.map((keyword, index) => (
              <span key={index} className="keyword-tag">{keyword}</span>
            ))}
          </div>
        </div>
        <div className="keywords-popup-footer">
          <p className="keywords-popup-hint">These keywords have been used to optimize your resume</p>
          <button className="keywords-popup-done" onClick={onClose} type="button">Done</button>
        </div>
      </div>
    </div>
  );
};

const ResumeBuilderPage = () => {
  const navigate = useNavigate();
  const templateSectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Accordion state - track which section is open
  const [openSection, setOpenSection] = useState(null);

  const handleAccordionToggle = (sectionId) => {
    setOpenSection(prev => prev === sectionId ? null : sectionId);
  };

  // Get user and credits from context
  const { user, credits, setCredits, fetchCredits } = useUser();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
  
  // State for job description and keywords
  const [jobDescription, setJobDescription] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [isExtractingKeywords, setIsExtractingKeywords] = useState(false);

  // Keywords popup state
  const [showKeywordsPopup, setShowKeywordsPopup] = useState(false);
  
  // State for workflow control
  const [isRightPanelEnabled, setIsRightPanelEnabled] = useState(false);
  const [isResumeOptimized, setIsResumeOptimized] = useState(false);
  
  // Credit and modal states
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [isSavingResume, setIsSavingResume] = useState(false);
  
  // State for personal information
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
    portfolio: ''
  });
  
  // State for professional summary
  const [professionalSummary, setProfessionalSummary] = useState('');
  
  // State for work experience
  const [workExperiences, setWorkExperiences] = useState([
    { id: 1, company: '', position: '', duration: '', description: '' }
  ]);
  
  // State for education
  const [education, setEducation] = useState([
    { id: 1, institution: '', degree: '', field: '', year: '' }
  ]);
  
  // State for skills
  const [skills, setSkills] = useState('');
  
  // State for projects
  const [projects, setProjects] = useState([
    { id: 1, name: '', description: '', technologies: '' }
  ]);
  
  // State for certifications
  const [certifications, setCertifications] = useState('');
  
  // State for template and document generation
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [templatePreview, setTemplatePreview] = useState(ProfessionalTemplatePreview);
  const [documentBlob, setDocumentBlob] = useState(null);
  const [forceRefreshDocx, setForceRefreshDocx] = useState(0);
  const [error, setError] = useState('');
  
  // New state for collapsible template section
  const [isTemplateSectionOpen, setIsTemplateSectionOpen] = useState(false);
  
  // Profile resume state
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'

  // New state for file upload
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [parseError, setParseError] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');

  // Fetch profile data
  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  // Update template preview when selected template changes
  useEffect(() => {
    const selected = RESUME_TEMPLATES.find(t => t.id === selectedTemplate);
    if (selected) {
      setTemplatePreview(selected.preview);
      setForceRefreshDocx(prev => prev + 1);
    }
  }, [selectedTemplate]);

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  const handleUseProfileResume = async () => {
    if (!profileData) return;
    if (!user) { setShowAuthModal(true); return; }
    if (credits < 1) { setShowInsufficientCreditsModal(true); return; }

    setIsParsingFile(true);
    setParseError('');
    setUploadedFileName(`${profileData.fullName || 'Profile'} (Profile)`);
    setIsResumeOptimized(false);

    try {
      const creditDeducted = await deductCredit();
      if (!creditDeducted) { setIsParsingFile(false); return; }

      const resumeText = buildResumeTextFromProfile(profileData);
      let parsedData;

      if (keywords.length > 0) {
        parsedData = await testResumeParsingWithText(resumeText, jobDescription, keywords);
      } else {
        parsedData = {
          personalInfo: {
            name: profileData.fullName || '',
            email: profileData.email || user?.email || '',
            phone: profileData.phone || '',
            location: profileData.location || '',
            linkedin: profileData.linkedIn || '',
            portfolio: profileData.portfolio || profileData.github || ''
          },
          summary: profileData.professionalSummary || '',
          workExperience: Array.isArray(profileData.workExperience) ? profileData.workExperience : [],
          education: Array.isArray(profileData.education) ? profileData.education : [],
          skills: profileData.skills || '',
          projects: Array.isArray(profileData.projects) ? profileData.projects : [],
          certifications: profileData.certifications || ''
        };
      }

      if (parsedData) {
        fillFormWithParsedData(parsedData);
        setParseError('');
        setIsRightPanelEnabled(true);
        setIsResumeOptimized(keywords.length > 0);
        setForceRefreshDocx(prev => prev + 1);
        setOpenSection('personal');
        if (fetchCredits) fetchCredits();
      }
    } catch (error) {
      console.error('Error processing profile resume:', error);
      setParseError(`Failed to process profile resume: ${error.message}`);
      setIsRightPanelEnabled(false);
      setIsResumeOptimized(false);
    } finally {
      setIsParsingFile(false);
    }
  };

  // Deduct credit function
  const deductCredit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resume/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setShowInsufficientCreditsModal(true);
          return false;
        }
        throw new Error(data.error || 'Failed to deduct credit');
      }

      if (setCredits) {
        setCredits(data.remainingCredits);
      }
      
      return true;
    } catch (error) {
      console.error('Error deducting credit:', error);
      setError('Failed to process credit transaction');
      return false;
    }
  };

  // Prepare resume data for the template system
  const prepareResumeData = useCallback(() => {
    const baseData = {
      personalInfo: {
        name: personalInfo.fullName,
        email: personalInfo.email,
        phone: personalInfo.phone,
        location: personalInfo.location,
        linkedin: personalInfo.linkedIn,
        portfolio: personalInfo.portfolio
      },
      fullName: personalInfo.fullName,
      name: personalInfo.fullName,
      email: personalInfo.email,
      phone: personalInfo.phone,
      location: personalInfo.location,
      linkedin: personalInfo.linkedIn,
      portfolio: personalInfo.portfolio,
      summary: professionalSummary,
      professionalSummary: professionalSummary,
      workExperience: workExperiences.map(exp => ({
        company: exp.company,
        position: exp.position,
        duration: exp.duration,
        description: exp.description
      })),
      workExperiences: workExperiences.map(exp => ({
        company: exp.company,
        position: exp.position,
        duration: exp.duration,
        description: exp.description
      })),
      education: education.map(edu => ({
        institution: edu.institution,
        degree: edu.degree,
        field: edu.field,
        year: edu.year
      })),
      skills: skills,
      projects: projects.map(proj => ({
        name: proj.name,
        description: proj.description,
        technologies: proj.technologies
      })),
      certifications: certifications
    };

    console.log('Prepared resume data:', baseData);
    return baseData;
  }, [personalInfo, professionalSummary, workExperiences, education, skills, projects, certifications]);


  // Extract keywords from job description
  const handleExtractKeywords = async () => {
    if (!jobDescription.trim()) {
      setError('Please enter a job description first');
      return;
    }

    setIsExtractingKeywords(true);
    setError('');

    try {
      const extractedKeywords = await extractKeywords(jobDescription);
      setKeywords(extractedKeywords);
    } catch (error) {
      console.error('Error extracting keywords:', error);
      setError('Failed to extract keywords. Please try again.');
    } finally {
      setIsExtractingKeywords(false);
    }
  };

  // Enhanced file upload handler with ATS optimization
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    if (!allowedTypes.includes(file.type)) {
      setParseError('Please upload a PDF or DOCX file only.');
      return;
    }

    if (!user) {
      setShowAuthModal(true);
      setParseError('Please login to use the resume builder');
      return;
    }

    if (credits < 1) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsParsingFile(true);
    setParseError('');
    setUploadedFileName(file.name);
    setIsResumeOptimized(false);

    try {
      const creditDeducted = await deductCredit();
      
      if (!creditDeducted) {
        setIsParsingFile(false);
        return;
      }

      const parsedData = await parseResumeFile(file, jobDescription, keywords);
      
      if (parsedData) {
        fillFormWithParsedData(parsedData);
        
        setParseError('');
        setIsRightPanelEnabled(true);
        setIsResumeOptimized(true);
        setForceRefreshDocx(prev => prev + 1);
        setOpenSection('personal'); // Open first section after optimization
        
        if (fetchCredits) {
          fetchCredits();
        }
      } else {
        throw new Error('No data could be extracted from the file');
      }
    } catch (error) {
      console.error('Error parsing resume file:', error);
      setParseError(`Failed to parse resume: ${error.message}`);
      setIsRightPanelEnabled(false);
      setIsResumeOptimized(false);
    } finally {
      setIsParsingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Fill form with parsed resume data
  const fillFormWithParsedData = (parsedData) => {
    console.log('Form filling - raw parsed data:', parsedData);
    
    if (parsedData.personalInfo) {
      setPersonalInfo({
        fullName: parsedData.personalInfo.name || parsedData.personalInfo.fullName || '',
        email: parsedData.personalInfo.email || '',
        phone: parsedData.personalInfo.phone || '',
        location: parsedData.personalInfo.location || parsedData.personalInfo.address || '',
        linkedIn: parsedData.personalInfo.linkedin || '',
        portfolio: parsedData.personalInfo.portfolio || parsedData.personalInfo.website || ''
      });
    }

    if (parsedData.summary || parsedData.professionalSummary) {
      setProfessionalSummary(parsedData.summary || parsedData.professionalSummary);
    }

    if (parsedData.workExperience && parsedData.workExperience.length > 0) {
      const formattedExperiences = parsedData.workExperience.map((exp, index) => ({
        id: index + 1,
        company: exp.company || exp.employer || '',
        position: exp.position || exp.title || exp.role || '',
        duration: exp.duration || exp.dates || exp.period || '',
        description: Array.isArray(exp.description) 
          ? exp.description.join('\n') 
          : exp.description || exp.responsibilities || ''
      }));
      setWorkExperiences(formattedExperiences);
    }

    if (parsedData.education && Array.isArray(parsedData.education) && parsedData.education.length > 0) {
      const validEducation = parsedData.education.filter(edu => 
        edu && 
        (edu.institution && edu.institution.trim() && edu.institution !== ', , ') &&
        (edu.degree && edu.degree.trim() && edu.degree !== ', , ')
      );
      
      if (validEducation.length > 0) {
        const formattedEducation = validEducation.map((edu, index) => ({
          id: index + 1,
          institution: edu.institution || edu.school || edu.university || '',
          degree: edu.degree || edu.qualification || '',
          field: edu.field || edu.major || edu.subject || '',
          year: edu.year || edu.dates || edu.graduation || ''
        }));
        setEducation(formattedEducation);
      }
    }

    if (parsedData.skills) {
      let skillsText = '';
      if (typeof parsedData.skills === 'object' && !Array.isArray(parsedData.skills)) {
        Object.entries(parsedData.skills).forEach(([category, skillList]) => {
          if (skillList && skillList.trim()) {
            skillsText += `• **${category}**: ${skillList}\n`;
          }
        });
        skillsText = skillsText.trim();
      } else {
        skillsText = Array.isArray(parsedData.skills) 
          ? parsedData.skills.join(', ') 
          : parsedData.skills;
      }
      setSkills(skillsText);
    }

    if (parsedData.projects && parsedData.projects.length > 0) {
      const formattedProjects = parsedData.projects.map((proj, index) => ({
        id: index + 1,
        name: proj.name || proj.title || '',
        description: Array.isArray(proj.description) 
          ? proj.description.join('\n') 
          : proj.description || '',
        technologies: Array.isArray(proj.technologies) 
          ? proj.technologies.join(', ') 
          : proj.technologies || proj.tech || ''
      }));
      setProjects(formattedProjects);
    }

    if (parsedData.certifications) {
      let certificationsText = '';
      if (Array.isArray(parsedData.certifications)) {
        const validCertifications = parsedData.certifications
          .filter(cert => {
            if (typeof cert === 'string') {
              return cert && cert.trim() !== '' && cert !== '[object Object]' && 
                     cert !== 'Professional Certification' && !cert.includes('[object Object]');
            } else if (typeof cert === 'object' && cert !== null) {
              return cert.name && cert.name.trim() !== '' && cert.name !== '[object Object]' && 
                     cert.name !== 'Professional Certification' && !cert.name.includes('[object Object]');
            }
            return false;
          })
          .map(cert => typeof cert === 'string' ? cert : (typeof cert === 'object' && cert.name ? cert.name : ''))
          .filter(cert => cert && cert.trim());
        certificationsText = validCertifications.join(', ');
      } else if (typeof parsedData.certifications === 'string') {
        if (parsedData.certifications !== '[object Object]' && 
            !parsedData.certifications.includes('[object Object]') &&
            parsedData.certifications !== 'Professional Certification') {
          certificationsText = parsedData.certifications;
        }
      }
      if (certificationsText && certificationsText.trim()) {
        setCertifications(certificationsText);
      } else {
        setCertifications('');
      }
    } else {
      setCertifications('');
    }
  };

  const handleDocumentChange = (blob) => {
    setDocumentBlob(blob);
  };

  const handleDownloadWord = async () => {
    if (documentBlob) {
      const fullName = personalInfo.fullName || 'Resume';
      const fileName = `${fullName.replace(/\s+/g, '_')}_Resume_ATS_Optimized.docx`;
      saveAs(documentBlob, fileName);
    } else {
      try {
        const resumeData = prepareResumeData();
        const blob = await templateService.generateFromTemplate(selectedTemplate, resumeData);
        const fullName = personalInfo.fullName || 'Resume';
        const fileName = `${fullName.replace(/\s+/g, '_')}_Resume_ATS_Optimized.docx`;
        saveAs(blob, fileName);
      } catch (error) {
        console.error('Error downloading document:', error);
        setError('Failed to download document. Please try again.');
      }
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setIsTemplateSectionOpen(false);
    if (templateSectionRef.current) {
      templateSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleTemplateSection = () => {
    setIsTemplateSectionOpen(!isTemplateSectionOpen);
  };

  const handleNavigateToHome = () => {
    navigate('/', { replace: true });
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAddWorkExperience = () => {
    setWorkExperiences(prev => [
      ...prev,
      { id: Date.now(), company: '', position: '', duration: '', description: '' }
    ]);
  };

  const handleRemoveWorkExperience = (id) => {
    if (workExperiences.length > 1) {
      setWorkExperiences(prev => prev.filter(exp => exp.id !== id));
    }
  };

  const handleWorkExperienceChange = (id, field, value) => {
    setWorkExperiences(prev => 
      prev.map(exp => exp.id === id ? { ...exp, [field]: value } : exp)
    );
  };

  const handleAddEducation = () => {
    setEducation(prev => [
      ...prev,
      { id: Date.now(), institution: '', degree: '', field: '', year: '' }
    ]);
  };

  const handleRemoveEducation = (id) => {
    if (education.length > 1) {
      setEducation(prev => prev.filter(edu => edu.id !== id));
    }
  };

  const handleEducationChange = (id, field, value) => {
    setEducation(prev => 
      prev.map(edu => edu.id === id ? { ...edu, [field]: value } : edu)
    );
  };

  const handleAddProject = () => {
    setProjects(prev => [
      ...prev,
      { id: Date.now(), name: '', description: '', technologies: '' }
    ]);
  };

  const handleRemoveProject = (id) => {
    if (projects.length > 1) {
      setProjects(prev => prev.filter(proj => proj.id !== id));
    }
  };

  const handleProjectChange = (id, field, value) => {
    setProjects(prev => 
      prev.map(proj => proj.id === id ? { ...proj, [field]: value } : proj)
    );
  };

  const handleClearAll = () => {
    setJobDescription('');
    setKeywords([]);
    setShowKeywordsPopup(false);
    setIsRightPanelEnabled(false);
    setIsResumeOptimized(false);
    setPersonalInfo({ fullName: '', email: '', phone: '', location: '', linkedIn: '', portfolio: '' });
    setProfessionalSummary('');
    setWorkExperiences([{ id: 1, company: '', position: '', duration: '', description: '' }]);
    setEducation([{ id: 1, institution: '', degree: '', field: '', year: '' }]);
    setSkills('');
    setProjects([{ id: 1, name: '', description: '', technologies: '' }]);
    setCertifications('');
    setError('');
    setDocumentBlob(null);
    setParseError('');
    setUploadedFileName('');
    setResumeChoice(null);
    setOpenSection('personal');
  };

  const isDisabled = !isRightPanelEnabled || isParsingFile || isExtractingKeywords;

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>
      
      <div className="resume-builder-page-container">
        <Header setShowAuthModal={setShowAuthModal} />
        <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

        {/* Keywords Popup */}
        {showKeywordsPopup && keywords.length > 0 && (
          <KeywordsPopup
            keywords={keywords}
            onClose={() => setShowKeywordsPopup(false)}
          />
        )}

        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to optimize your resume.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="resume-builder-page-main">
          <div className="li-hero">
            <div className="li-hero-badge">🚀 AI-Powered</div>
            <h1 className="li-hero-title">
              AI Resume <span className="li-gradient-text">Builder</span>
            </h1>
            <p className="li-hero-subtitle">
              Train with AI-powered interview questions and prepare effectively for your next interview.
            </p>
          </div>

          <div className="resume-builder-main-container">
            <div className="resume-builder-content">

              {/* Left Side - Job Description and File Upload */}
              <div className="left-panel">
                <div className="job-description-section">
                  <div className="job-description-container glass">
                    <div className="section-header">
                      <h3>Job Description</h3>
                      <button 
                        onClick={handleClearAll} 
                        className="clear-button"
                        disabled={isExtractingKeywords || isParsingFile}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="form-group-RBP JD-textarea">
                      <textarea
                        value={jobDescription}
                        onChange={(e) => {
                          setJobDescription(e.target.value);
                          setError('');
                        }}
                        placeholder="Paste the job description here to extract relevant keywords..."
                        rows="8"
                        disabled={isExtractingKeywords || isParsingFile}
                      ></textarea>
                    </div>
                    <div className="section-footer">
                      <button 
                        onClick={handleExtractKeywords} 
                        className="extract-btn"
                        disabled={isExtractingKeywords || isParsingFile || !jobDescription.trim()}
                      >
                        {isExtractingKeywords ? (
                          <span className="loading">
                            <span className="typing-indicator">
                              <span></span><span></span><span></span>
                            </span>
                            Extracting...
                          </span>
                        ) : 'Extract Keywords'}
                      </button>

                      {/* Show Keywords checkbox — only visible after keywords are generated */}
                      {keywords.length > 0 && (
                        <label className="show-keywords-checkbox-label">
                          <input
                            type="checkbox"
                            className="show-keywords-checkbox"
                            checked={showKeywordsPopup}
                            onChange={(e) => setShowKeywordsPopup(e.target.checked)}
                          />
                          <span className="show-keywords-checkbox-text">
                            Show Keywords
                            <span className="show-keywords-count">{keywords.length}</span>
                          </span>
                        </label>
                      )}

                      <div className="character-count">{jobDescription.length} characters</div>
                    </div>
                  </div>
                </div>

                <div className="file-upload-section">
                  <div className="file-upload-container glass">
                    <div className="section-header">
                      <h3>Resume for ATS Optimization</h3>
                      {isResumeOptimized && (
                        <span className="optimization-status">✅ ATS Optimized</span>
                      )}
                    </div>
                    <div className="upload-area">
                      {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                        <>
                          <div className="resume-choice-buttons">
                            <button
                              type="button"
                              onClick={() => setResumeChoice('profile')}
                              className={`resume-choice-btn${resumeChoice === 'profile' ? ' resume-choice-btn-active' : ''}`}
                              disabled={isParsingFile || isExtractingKeywords}
                            >
                              <div className="resume-choice-label">✅ Use Profile Resume</div>
                              {profileData.fullName && <div className="resume-choice-sublabel">{profileData.fullName}</div>}
                            </button>
                            <button
                              type="button"
                              onClick={() => setResumeChoice('upload')}
                              className={`resume-choice-btn${resumeChoice === 'upload' ? ' resume-choice-btn-active' : ''}`}
                              disabled={isParsingFile || isExtractingKeywords}
                            >
                              <div className="resume-choice-label">📄 Upload New Resume</div>
                              <div className="resume-choice-sublabel">Choose a different file</div>
                            </button>
                          </div>

                          {resumeChoice === 'profile' && (
                            <>
                              <button
                                onClick={handleUseProfileResume}
                                className="file-upload-label"
                                disabled={isParsingFile || isExtractingKeywords || keywords.length === 0}
                                style={{ opacity: keywords.length === 0 ? 0.5 : 1, cursor: keywords.length === 0 ? 'not-allowed' : 'pointer' }}
                              >
                                {isParsingFile ? (
                                  <span className="loading">
                                    <span className="typing-indicator"><span></span><span></span><span></span></span>
                                    Optimizing Resume...
                                  </span>
                                ) : <>🚀 Optimize with Profile Resume</>}
                              </button>
                              <p className="upload-hint">
                                {keywords.length === 0
                                  ? "Extract keywords first to ATS-optimize your profile resume"
                                  : "Click to ATS-optimize your profile resume with extracted keywords"}
                              </p>
                            </>
                          )}

                          {resumeChoice === 'upload' && (
                            <>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.docx,.doc"
                                onChange={handleFileUpload}
                                disabled={isParsingFile || isExtractingKeywords || keywords.length === 0}
                                style={{ display: 'none' }}
                              />
                              <button
                                onClick={() => keywords.length > 0 ? fileInputRef.current?.click() : null}
                                className="file-upload-label"
                                disabled={isParsingFile || isExtractingKeywords || keywords.length === 0}
                                style={{ opacity: keywords.length === 0 ? 0.5 : 1, cursor: keywords.length === 0 ? 'not-allowed' : 'pointer' }}
                              >
                                {isParsingFile ? (
                                  <span className="loading">
                                    <span className="typing-indicator"><span></span><span></span><span></span></span>
                                    Optimizing Resume...
                                  </span>
                                ) : <>📄 Upload & Optimize Resume</>}
                              </button>
                              <p className="upload-hint">
                                {keywords.length === 0
                                  ? "Extract keywords first, then upload resume for automatic ATS optimization"
                                  : "Upload resume to auto-optimize with keywords and job requirements"}
                              </p>
                            </>
                          )}

                          {!resumeChoice && (
                            <p className="upload-hint">Select an option above to get started</p>
                          )}
                        </>
                      ) : (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.docx,.doc"
                            onChange={handleFileUpload}
                            disabled={isParsingFile || isExtractingKeywords || keywords.length === 0}
                            style={{ display: 'none' }}
                          />
                          <button
                            onClick={() => keywords.length > 0 ? fileInputRef.current?.click() : null}
                            className="file-upload-label"
                            disabled={isParsingFile || isExtractingKeywords || keywords.length === 0}
                            style={{ opacity: keywords.length === 0 ? 0.5 : 1, cursor: keywords.length === 0 ? 'not-allowed' : 'pointer' }}
                          >
                            {isParsingFile ? (
                              <span className="loading">
                                <span className="typing-indicator"><span></span><span></span><span></span></span>
                                Optimizing Resume...
                              </span>
                            ) : <>📄 Upload & Optimize Resume</>}
                          </button>
                          <p className="upload-hint">
                            {keywords.length === 0
                              ? "Extract keywords first, then upload resume for automatic ATS optimization"
                              : "Upload resume to auto-optimize with keywords and job requirements"}
                          </p>
                        </>
                      )}

                      {uploadedFileName && (
                        <p className="uploaded-file">✅ Processed: {uploadedFileName}</p>
                      )}
                      {parseError && (
                        <div className="parse-error">⚠️ {parseError}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Accordion Resume Form */}
              <div className="right-panel">
                {isResumeOptimized && (
                  <div className="ats-status-banner">
                    <span className="ats-banner-icon">🎯</span>
                    <div>
                      <strong>Resume ATS Optimized</strong>
                      <p>Your resume has been enhanced with keywords. Review and edit as needed before downloading.</p>
                    </div>
                  </div>
                )}

                <div className="accordion-container">

                  {/* Personal Information */}
                  <AccordionSection
                    id="personal"
                    title="Personal Information"
                    icon="👤"
                    isOpen={openSection === 'personal'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                  >
                    <div className="form-grid">
                      <div className="form-group-RBP">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          value={personalInfo.fullName}
                          onChange={(e) => handlePersonalInfoChange('fullName', e.target.value)}
                          placeholder="John Doe"
                          disabled={isDisabled}
                        />
                      </div>
                      <div className="form-group-RBP">
                        <label>Email</label>
                        <input
                          type="email"
                          value={personalInfo.email}
                          onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                          placeholder="john@example.com"
                          disabled={isDisabled}
                        />
                      </div>
                      <div className="form-group-RBP">
                        <label>Phone</label>
                        <input
                          type="tel"
                          value={personalInfo.phone}
                          onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                          placeholder="+1 (555) 000-0000"
                          disabled={isDisabled}
                        />
                      </div>
                      <div className="form-group-RBP">
                        <label>Location</label>
                        <input
                          type="text"
                          value={personalInfo.location}
                          onChange={(e) => handlePersonalInfoChange('location', e.target.value)}
                          placeholder="City, State"
                          disabled={isDisabled}
                        />
                      </div>
                      <div className="form-group-RBP">
                        <label>LinkedIn</label>
                        <input
                          type="url"
                          value={personalInfo.linkedIn}
                          onChange={(e) => handlePersonalInfoChange('linkedIn', e.target.value)}
                          placeholder="linkedin.com/in/johndoe"
                          disabled={isDisabled}
                        />
                      </div>
                      <div className="form-group-RBP">
                        <label>Portfolio</label>
                        <input
                          type="url"
                          value={personalInfo.portfolio}
                          onChange={(e) => handlePersonalInfoChange('portfolio', e.target.value)}
                          placeholder="yourwebsite.com"
                          disabled={isDisabled}
                        />
                      </div>
                    </div>
                  </AccordionSection>

                  {/* Professional Summary */}
                  <AccordionSection
                    id="summary"
                    title="Professional Summary"
                    icon="📝"
                    isOpen={openSection === 'summary'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                    badge={isResumeOptimized ? '✨ Optimized' : null}
                  >
                    <div className="form-group-RBP">
                      <textarea
                        value={professionalSummary}
                        onChange={(e) => setProfessionalSummary(e.target.value)}
                        placeholder="Professional summary optimized with job keywords..."
                        rows="5"
                        disabled={isDisabled}
                      ></textarea>
                    </div>
                  </AccordionSection>

                  {/* Work Experience */}
                  <AccordionSection
                    id="experience"
                    title="Work Experience"
                    icon="💼"
                    isOpen={openSection === 'experience'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                    badge={isResumeOptimized ? '✨ Optimized' : null}
                  >
                    <div className="accordion-section-actions">
                      <button 
                        onClick={handleAddWorkExperience} 
                        className="add-button"
                        disabled={isDisabled}
                      >
                        + Add Experience
                      </button>
                    </div>
                    {workExperiences.map((exp, index) => (
                      <div key={exp.id} className="accordion-sub-item">
                        <div className="item-header">
                          <h4>Experience #{index + 1}</h4>
                          {workExperiences.length > 1 && (
                            <button 
                              onClick={() => handleRemoveWorkExperience(exp.id)}
                              className="remove-button"
                              disabled={isDisabled}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="form-grid">
                          <div className="form-group-RBP">
                            <label>Company</label>
                            <input type="text" value={exp.company}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'company', e.target.value)}
                              disabled={isDisabled} />
                          </div>
                          <div className="form-group-RBP">
                            <label>Position</label>
                            <input type="text" value={exp.position}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'position', e.target.value)}
                              disabled={isDisabled} />
                          </div>
                          <div className="form-group-RBP">
                            <label>Duration</label>
                            <input type="text" value={exp.duration}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'duration', e.target.value)}
                              placeholder="e.g., Jan 2020 - Present"
                              disabled={isDisabled} />
                          </div>
                        </div>
                        <div className="form-group-RBP">
                          <label>Description</label>
                          <textarea
                            value={exp.description}
                            onChange={(e) => handleWorkExperienceChange(exp.id, 'description', e.target.value)}
                            placeholder="ATS-optimized bullet points with achievements..."
                            rows="4"
                            disabled={isDisabled}
                          ></textarea>
                          <small className="field-hint">Use bullet points (•) for better formatting in Word document</small>
                        </div>
                      </div>
                    ))}
                  </AccordionSection>

                  {/* Education */}
                  <AccordionSection
                    id="education"
                    title="Education"
                    icon="🎓"
                    isOpen={openSection === 'education'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                  >
                    <div className="accordion-section-actions">
                      <button onClick={handleAddEducation} className="add-button" disabled={isDisabled}>
                        + Add Education
                      </button>
                    </div>
                    {education.map((edu, index) => (
                      <div key={edu.id} className="accordion-sub-item">
                        <div className="item-header">
                          <h4>Education #{index + 1}</h4>
                          {education.length > 1 && (
                            <button onClick={() => handleRemoveEducation(edu.id)} className="remove-button" disabled={isDisabled}>
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="form-grid">
                          <div className="form-group-RBP">
                            <label>Institution</label>
                            <input type="text" value={edu.institution}
                              onChange={(e) => handleEducationChange(edu.id, 'institution', e.target.value)}
                              disabled={isDisabled} />
                          </div>
                          <div className="form-group-RBP">
                            <label>Degree</label>
                            <input type="text" value={edu.degree}
                              onChange={(e) => handleEducationChange(edu.id, 'degree', e.target.value)}
                              placeholder="e.g., Bachelor of Science"
                              disabled={isDisabled} />
                          </div>
                          <div className="form-group-RBP">
                            <label>Field of Study</label>
                            <input type="text" value={edu.field}
                              onChange={(e) => handleEducationChange(edu.id, 'field', e.target.value)}
                              placeholder="e.g., Computer Science"
                              disabled={isDisabled} />
                          </div>
                          <div className="form-group-RBP">
                            <label>Year</label>
                            <input type="text" value={edu.year}
                              onChange={(e) => handleEducationChange(edu.id, 'year', e.target.value)}
                              placeholder="e.g., 2018-2022"
                              disabled={isDisabled} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </AccordionSection>

                  {/* Skills */}
                  <AccordionSection
                    id="skills"
                    title="Skills"
                    icon="⚡"
                    isOpen={openSection === 'skills'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                    badge={isResumeOptimized ? '✨ Optimized' : null}
                  >
                    <div className="form-group-RBP">
                      <textarea
                        value={skills}
                        onChange={(e) => setSkills(e.target.value)}
                        placeholder="ATS-optimized skills with job-relevant keywords..."
                        rows="4"
                        disabled={isDisabled}
                      ></textarea>
                    </div>
                  </AccordionSection>

                  {/* Projects */}
                  <AccordionSection
                    id="projects"
                    title="Projects"
                    icon="🚀"
                    isOpen={openSection === 'projects'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                    badge={isResumeOptimized ? '✨ Optimized' : null}
                  >
                    <div className="accordion-section-actions">
                      <button onClick={handleAddProject} className="add-button" disabled={isDisabled}>
                        + Add Project
                      </button>
                    </div>
                    {projects.map((proj, index) => (
                      <div key={proj.id} className="accordion-sub-item">
                        <div className="item-header">
                          <h4>Project #{index + 1}</h4>
                          {projects.length > 1 && (
                            <button onClick={() => handleRemoveProject(proj.id)} className="remove-button" disabled={isDisabled}>
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="form-grid">
                          <div className="form-group-RBP">
                            <label>Project Name</label>
                            <input type="text" value={proj.name}
                              onChange={(e) => handleProjectChange(proj.id, 'name', e.target.value)}
                              disabled={isDisabled} />
                          </div>
                          <div className="form-group-RBP">
                            <label>Technologies</label>
                            <input type="text" value={proj.technologies}
                              onChange={(e) => handleProjectChange(proj.id, 'technologies', e.target.value)}
                              placeholder="e.g., React, Node.js, MongoDB"
                              disabled={isDisabled} />
                          </div>
                        </div>
                        <div className="form-group-RBP">
                          <label>Description</label>
                          <textarea
                            value={proj.description}
                            onChange={(e) => handleProjectChange(proj.id, 'description', e.target.value)}
                            placeholder="ATS-optimized project description with bullet points..."
                            rows="4"
                            disabled={isDisabled}
                          ></textarea>
                          <small className="field-hint">Use bullet points (•) for better formatting in Word document</small>
                        </div>
                      </div>
                    ))}
                  </AccordionSection>

                  {/* Certifications */}
                  <AccordionSection
                    id="certifications"
                    title="Certifications"
                    icon="🏆"
                    isOpen={openSection === 'certifications'}
                    onToggle={handleAccordionToggle}
                    disabled={isDisabled}
                  >
                    <div className="form-group-RBP">
                      <textarea
                        value={certifications}
                        onChange={(e) => setCertifications(e.target.value)}
                        placeholder="List certifications (e.g., AWS Certified Developer, Google Analytics)..."
                        rows="3"
                        disabled={isDisabled}
                      ></textarea>
                    </div>
                  </AccordionSection>

                </div>

                {/* Download Section */}
                <div className="download-section">
                  <button 
                    onClick={handleDownloadWord} 
                    className="download-button primary-download"
                    disabled={isDisabled || !personalInfo.fullName}
                  >
                    📄 Download ATS-Optimized Resume
                  </button>
                  <p className="download-hint">
                    {isResumeOptimized 
                      ? "Your resume is ATS-optimized and ready to download!" 
                      : "Upload and optimize your resume first"}
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message glass">{error}</div>
            )}
          </div>
        </main>

        <section className="info-section glass">
          <h2 className="section-title">Why Use ATS-Optimized Resumes?</h2>
          <div className="info-grid">
            <div className="info-card glass">
              <div className="info-icon">🎯</div>
              <h3>Beat Applicant Tracking Systems</h3>
              <p>90% of large companies use ATS software to screen resumes. Our tool ensures your resume passes through these systems by strategically incorporating relevant keywords from job descriptions.</p>
            </div>
            <div className="info-card glass">
              <div className="info-icon">⚡</div>
              <h3>Smart Keyword Extraction</h3>
              <p>Our AI analyzes job descriptions to identify critical keywords, required skills, and qualifications that ATS systems look for, then optimizes your resume accordingly.</p>
            </div>
            <div className="info-card glass">
              <div className="info-icon">📈</div>
              <h3>Increase Interview Chances</h3>
              <p>ATS-optimized resumes have up to 75% higher chance of reaching human recruiters. Stand out from the competition with a resume designed to pass automated screening.</p>
            </div>
          </div>
        </section>

        <section className="how-it-works glass">
          <h2 className="section-title">How Our ATS Optimization Works</h2>
          <div className="steps-grid">
            <div className="step-card glass">
              <div className="step-number">1</div>
              <h4>Paste Job Description</h4>
              <p>Copy the job posting you're applying for and paste it into our tool. Our AI will analyze the requirements, responsibilities, and key qualifications.</p>
            </div>
            <div className="step-card glass">
              <div className="step-number">2</div>
              <h4>Extract Keywords</h4>
              <p>Click "Extract Keywords" and our system identifies the most important terms, skills, and phrases that ATS systems will scan for in your resume.</p>
            </div>
            <div className="step-card glass">
              <div className="step-number">3</div>
              <h4>Upload Your Resume</h4>
              <p>Upload your existing resume in PDF or DOCX format. Our AI will parse your information and intelligently integrate the extracted keywords.</p>
            </div>
            <div className="step-card glass">
              <div className="step-number">4</div>
              <h4>Review & Download</h4>
              <p>Review your ATS-optimized resume with strategically placed keywords. Edit any section as needed, then download your enhanced resume ready for submission.</p>
            </div>
          </div>
        </section>

        <section className="ats-tips glass">
          <h2 className="section-title">ATS Optimization Best Practices</h2>
          <div className="tips-grid">
            <div className="tip-card glass">
              <div className="tip-header"><span className="tip-icon">✓</span><h4>Use Standard Formatting</h4></div>
              <p>Stick to simple, clean layouts without tables, text boxes, or complex formatting. ATS systems read text sequentially and can struggle with creative designs.</p>
            </div>
            <div className="tip-card glass">
              <div className="tip-header"><span className="tip-icon">✓</span><h4>Include Relevant Keywords</h4></div>
              <p>Mirror the language from the job description. If they say "project management," don't just write "managed projects." Use the exact terminology they use.</p>
            </div>
            <div className="tip-card glass">
              <div className="tip-header"><span className="tip-icon">✓</span><h4>Use Standard Headings</h4></div>
              <p>Use conventional section titles like "Work Experience," "Education," and "Skills." Creative headings may confuse ATS systems and cause them to miss important information.</p>
            </div>
            <div className="tip-card glass">
              <div className="tip-header"><span className="tip-icon">✓</span><h4>Spell Out Acronyms</h4></div>
              <p>Write out acronyms at least once, followed by the abbreviation in parentheses. For example: "Search Engine Optimization (SEO)" ensures you're found regardless of how the job description is written.</p>
            </div>
            <div className="tip-card glass">
              <div className="tip-header"><span className="tip-icon">✓</span><h4>Use Bullet Points</h4></div>
              <p>Bullet points are ATS-friendly and improve readability. They help both automated systems and human recruiters quickly scan your achievements and responsibilities.</p>
            </div>
            <div className="tip-card glass">
              <div className="tip-header"><span className="tip-icon">✓</span><h4>Quantify Achievements</h4></div>
              <p>Include numbers, percentages, and metrics. ATS systems can recognize and prioritize quantifiable accomplishments, and recruiters love seeing concrete results.</p>
            </div>
          </div>
        </section>

        <section className="faq-section glass">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item glass">
              <h4>What is an ATS and why does it matter?</h4>
              <p>An Applicant Tracking System (ATS) is software used by employers to collect, scan, and rank job applications. About 75% of resumes never reach human eyes because they're filtered out by ATS. Optimizing for ATS is crucial to getting your resume seen.</p>
            </div>
            <div className="faq-item glass">
              <h4>How does keyword extraction work?</h4>
              <p>Our AI analyzes the job description to identify critical terms, required skills, qualifications, and industry-specific terminology. It then categorizes these keywords and strategically incorporates them into your resume while maintaining natural readability.</p>
            </div>
            <div className="faq-item glass">
              <h4>Will my resume still sound natural?</h4>
              <p>Absolutely. Our optimization process integrates keywords naturally into your existing content. The result is a resume that passes ATS screening while still reading professionally and authentically to human recruiters.</p>
            </div>
            <div className="faq-item glass">
              <h4>Can I edit the optimized resume?</h4>
              <p>Yes! After optimization, you can review and edit any section of your resume. All fields remain editable, allowing you to fine-tune the content while keeping the ATS-friendly structure and keyword integration.</p>
            </div>
            <div className="faq-item glass">
              <h4>What file formats are supported?</h4>
              <p>You can upload resumes in PDF, DOC, or DOCX format. The optimized resume is generated as a DOCX file, which is the most ATS-compatible format and can be easily edited in Microsoft Word or Google Docs.</p>
            </div>
            <div className="faq-item glass">
              <h4>Should I create a different resume for each job?</h4>
              <p>Yes, we highly recommend it. Each job has unique requirements and keywords. Using our tool to create tailored, ATS-optimized resumes for each application significantly increases your chances of getting interviews.</p>
            </div>
          </div>
        </section>

        <Footer></Footer>
      </div>
    </div>
  );
};

export default ResumeBuilderPage;